// LandingPage.jsx — the "living ledger" landing page.
// All prices/limits/trial come from pricingConfig.js (single source of truth).
import React, { useEffect, useRef, useState } from 'react';
import './landing.css';
import { PRICING_TIERS, SERVICE_PROVIDER_PRICING, TRIAL_DAYS, TENANT_FEE, fmtPrice } from './pricingConfig';
import { fetchPlans, FALLBACK_PLANS, dollars } from './planService';

const FEED_EVENTS = [
  { ic: '$', cls: 'g', html: <><b className="mono">$1,500</b> rent received</>, sub: '123 Main St · 2m' },
  { ic: '✉', cls: 'p', html: <>Receipt sent to John</>, sub: 'automatic · 2m' },
  { ic: '🔧', cls: '', html: <>New repair request</>, sub: '+2 photos · 14m' },
  { ic: '$', cls: 'g', html: <>Late fee applied</>, sub: '789 Pine Rd · 1h' },
  { ic: '📄', cls: 'p', html: <>Lease signed &amp; filed</>, sub: 'New tenant · 3h' },
  { ic: '⭐', cls: '', html: <>Plumber rated 5 stars</>, sub: 'by you · 1d' },
];

export default function LandingPage({ onAuth, navigateTo }) {
  // Live plans (v4.14): numbers come from the platform's single source so a
  // console edit changes this page too. Static import remains the instant
  // fallback — pricing never renders blank.
  const [livePlans, setLivePlans] = React.useState(FALLBACK_PLANS); // cents, always — no mixed-unit frame
  React.useEffect(() => { fetchPlans().then(setLivePlans).catch(() => {}); }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  // The city pages people actually type into search — one tap from home.
  const CITY_PAGES = [['Baltimore', '/baltimore'], ['Philadelphia', '/philadelphia'], ['Wilmington, DE', '/wilmington']];
  // Every public page, one menu — nobody should have to memorize a URL.
  const EXPLORE_PAGES = [
    ['How it works', '/how-it-works'],
    ['Verify a document', '/verify'],
    ['Guides & tutorials', '/guides'],
    ['Security practices', '/security'],
    ['Government portal', '/gov'],
    ['For housing agencies', '/for-agencies'],
    ['Commercial properties', '/commercial'],
    ['Extended stay', '/extended-stay'],
    ['What’s new', '/changelog'],
  ];
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const heroRef = useRef(null);
  const spotRef = useRef(null);
  const stageRef = useRef(null);
  const navRef = useRef(null);
  const bigNumRef = useRef(null);
  const bigMetaRef = useRef(null);
  const pinePillRef = useRef(null);
  const rootRef = useRef(null);
  const [feed, setFeed] = useState([0, 1, 2]); // indices into FEED_EVENTS

  // All motion wiring lives in one effect with full cleanup.
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hero = heroRef.current, spot = spotRef.current, navPill = navRef.current;
    const cleanups = [];

    // cursor spotlight + parallax
    let tx = 0, ty = 0, cx = 0, cy = 0, raf;
    const cards = stageRef.current ? [...stageRef.current.querySelectorAll('.card3d')] : [];
    const onMove = (e) => {
      const r = hero.getBoundingClientRect();
      spot.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100) + '%');
      spot.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100) + '%');
      tx = (e.clientX - r.left) / r.width - 0.5;
      ty = (e.clientY - r.top) / r.height - 0.5;
    };
    hero.addEventListener('mousemove', onMove);
    cleanups.push(() => hero.removeEventListener('mousemove', onMove));
    if (!reduced) {
      const loop = () => {
        cx += (tx - cx) * 0.06; cy += (ty - cy) * 0.06;
        cards.forEach((c) => {
          const d = +c.dataset.depth;
          c.style.translate = `${-cx * d}px ${-cy * d}px`;
          if (c.classList.contains('main-card')) c.style.rotate = `${cx * 2}deg`;
        });
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      cleanups.push(() => cancelAnimationFrame(raf));
    }

    // the orchestrated moment: Mike pays
    const finishPaid = () => {
      if (bigNumRef.current) bigNumRef.current.textContent = '$5,500';
      if (bigMetaRef.current) bigMetaRef.current.textContent = '3 of 3 payments in';
      if (pinePillRef.current) {
        pinePillRef.current.className = 'pill ok';
        pinePillRef.current.textContent = 'Paid · just now';
      }
    };
    if (reduced) finishPaid();
    else {
      const t = setTimeout(() => {
        const s = 3300, e2 = 5500, t0 = performance.now(), dur = 1300;
        const tick = (t1) => {
          const p = Math.min((t1 - t0) / dur, 1), ease = 1 - Math.pow(1 - p, 3);
          if (bigNumRef.current) bigNumRef.current.textContent = '$' + Math.round(s + (e2 - s) * ease).toLocaleString();
          if (p < 1) requestAnimationFrame(tick); else finishPaid();
        };
        requestAnimationFrame(tick);
      }, 1800);
      cleanups.push(() => clearTimeout(t));
    }

    // live feed rotation
    if (!reduced) {
      let i = 3;
      const iv = setInterval(() => {
        setFeed((prev) => [i++ % FEED_EVENTS.length, ...prev.slice(0, 2)]);
      }, 3600);
      cleanups.push(() => clearInterval(iv));
    }

    // adaptive nav tone over light sections
    const lightSections = [...rootRef.current.querySelectorAll('.light')];
    const navTone = () => {
      const y = 44;
      const lite = lightSections.some((s) => {
        const r = s.getBoundingClientRect();
        return r.top < y && r.bottom > y;
      });
      navPill.classList.toggle('lite', lite);
    };
    window.addEventListener('scroll', navTone, { passive: true });
    navTone();
    cleanups.push(() => window.removeEventListener('scroll', navTone));

    // scroll reveals + counters
    const io = new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      en.target.classList.add('in');
      en.target.querySelectorAll('[data-count]').forEach((n) => {
        const end = +n.dataset.count, pre = n.dataset.prefix || '', suf = n.dataset.suffix || '';
        if (reduced) { n.textContent = pre + end + suf; return; }
        const t0 = performance.now();
        const c = (t1) => {
          const p = Math.min((t1 - t0) / 1100, 1);
          n.textContent = pre + Math.round(end * (1 - Math.pow(1 - p, 3))) + suf;
          if (p < 1) requestAnimationFrame(c);
        };
        requestAnimationFrame(c);
      });
      io.unobserve(en.target);
    }), { threshold: 0.22 });
    rootRef.current.querySelectorAll('.rv, .cell').forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());

    return () => cleanups.forEach((fn) => fn());
  }, []);

  const starter = livePlans.landlord_starter;
  const pro = livePlans.landlord_professional;
  const biz = livePlans.landlord_business;

  return (
    <div className="lp" ref={rootRef}>
      <div className="grain"></div>

      <nav className="lnav">
        <div className="nav-pill" ref={navRef}>
          <button className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/propmn-logo.png" alt="PropMN" />
          </button>
          <div className="nav-links">
            <button onClick={() => scrollTo('features')}>Features</button>
            <button onClick={() => scrollTo('pricing-teaser')}>Pricing</button>
            <button onClick={() => navigateTo('service-providers')}>Service pros</button>
            <div className="nav-city">
              <button onClick={() => { setCityOpen(o => !o); setExploreOpen(false); }}>Your city ▾</button>
              {cityOpen && (
                <div className="city-drop">
                  {CITY_PAGES.map(([label, href]) => (
                    <a key={href} href={href}>{label}</a>
                  ))}
                </div>
              )}
            </div>
            <div className="nav-city">
              <button onClick={() => { setExploreOpen(o => !o); setCityOpen(false); }}>Explore ▾</button>
              {exploreOpen && (
                <div className="city-drop">
                  {EXPLORE_PAGES.map(([label, href]) => (
                    <a key={href} href={href}>{label}</a>
                  ))}
                </div>
              )}
            </div>
            <button className="btn-login" onClick={() => onAuth('landlord', 'login')}>Log in</button>
          </div>
          <button className="nav-burger" aria-label="Menu" onClick={() => setMenuOpen(o => !o)}>☰</button>
          <button className="btn-nav" onClick={() => onAuth('landlord', 'register')}>Start free</button>
        </div>
        {menuOpen && (
          <div className="nav-drop">
            <button onClick={() => { setMenuOpen(false); scrollTo('features'); }}>Features</button>
            <button onClick={() => { setMenuOpen(false); scrollTo('pricing-teaser'); }}>Pricing</button>
            <button onClick={() => { setMenuOpen(false); navigateTo('service-providers'); }}>Service pros</button>
            <div className="drop-label">Your city</div>
            <div className="drop-grid three">
              {CITY_PAGES.map(([label, href]) => (
                <button key={href} onClick={() => { window.location.href = href; }}>{label.replace(', DE', '')}</button>
              ))}
            </div>
            <div className="drop-label">Explore</div>
            <div className="drop-grid">
              {EXPLORE_PAGES.map(([label, href]) => (
                <button key={href} onClick={() => { window.location.href = href; }}>{label}</button>
              ))}
            </div>
            <div className="drop-actions">
              <button className="drop-login" onClick={() => { setMenuOpen(false); onAuth('landlord', 'login'); }}>Log in</button>
              <button className="drop-cta" onClick={() => { setMenuOpen(false); onAuth('landlord', 'register'); }}>Start free</button>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <header className="hero" ref={heroRef}>
        <div className="aurora"><div className="blob b1"></div><div className="blob b2"></div><div className="blob b3"></div></div>
        <div className="gridfloor"></div>
        <div className="spot" ref={spotRef}></div>

        <div className="wrap hero-grid">
          <div>
            <span className="eyebrow"><span className="live"></span>For landlords with 1–{(livePlans.landlord_enterprise?.maxProperties ?? FALLBACK_PLANS.landlord_enterprise.maxProperties)}+ doors</span>
            <h1>
              <span className="hline">
                <span className="w" style={{ animationDelay: '.05s' }}>Every</span>{' '}
                <span className="w" style={{ animationDelay: '.13s' }}>door</span>{' '}
                <span className="w" style={{ animationDelay: '.21s' }}>you</span>{' '}
                <span className="w" style={{ animationDelay: '.29s' }}>own.</span>
              </span>
              <span className="hline">
                <span className="w" style={{ animationDelay: '.42s' }}>One</span>{' '}
                <span className="w grad serif" style={{ animationDelay: '.52s' }}>living</span>{' '}
                <span className="w grad" style={{ animationDelay: '.62s' }}>ledger.</span>
              </span>
            </h1>
            <p className="sub">Houses, salon suites, offices, voucher housing — PropMN screens applicants, collects rent, splits assistance payments, renews leases, and files every receipt. Any state, any door. Free for tenants.</p>
            <div className="hero-ctas">
              <button className="btn-hero" onClick={() => onAuth('landlord', 'register')}>Start your {TRIAL_DAYS}-day free trial</button>
              <a className="btn-quiet" href="#features">See it in action <span>↓</span></a>
            </div>
            <p className="hero-meta">From <span className="mono">{dollars(starter.price)}/mo</span> after {TRIAL_DAYS} days · Tenants pay <span className="mono">${TENANT_FEE}</span>, always</p>
          </div>

          {/* the living ledger */}
          <div className="stage" ref={stageRef}>
            <div className="card3d glass main-card" data-depth="14">
              <div className="mc-head">
                <span className="t"><svg width="14" height="14" viewBox="0 0 26 26"><path d="M13 2.5 2.8 11.2v11.3c0 .8.7 1.5 1.5 1.5h6.2v-7.5h5v7.5h6.2c.8 0 1.5-.7 1.5-1.5V11.2L13 2.5z" fill="#7C5CFF" /></svg> June 2026 · Portfolio</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fade2)' }}>LIVE</span>
              </div>
              <div className="mc-total">
                <div className="l">Collected this month</div>
                <div className="a" ref={bigNumRef}>$3,300</div>
                <div className="m" ref={bigMetaRef}>2 of 3 payments in</div>
              </div>
              <svg className="spark" viewBox="0 0 340 64" preserveAspectRatio="none">
                <defs><linearGradient id="lpsg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="rgba(45,215,146,.28)" /><stop offset="1" stopColor="rgba(45,215,146,0)" />
                </linearGradient></defs>
                <path className="fill" d="M0 58 L0 44 C30 40 50 46 80 38 C110 30 130 36 160 26 C190 18 215 24 245 14 C275 6 305 12 340 4 L340 58 Z" />
                <path className="line" d="M0 44 C30 40 50 46 80 38 C110 30 130 36 160 26 C190 18 215 24 245 14 C275 6 305 12 340 4" />
              </svg>
              <div className="mc-rows">
                <div className="mrow"><div className="w"><b>123 Main St</b><small>John Smith</small></div><div className="a">$1,500<br /><span className="pill ok">Paid · Jun 1</span></div></div>
                <div className="mrow"><div className="w"><b>456 Oak Ave</b><small>Sarah Johnson</small></div><div className="a">$1,800<br /><span className="pill ok">Paid · Jun 1</span></div></div>
                <div className="mrow"><div className="w"><b>789 Pine Rd</b><small>Mike Wilson</small></div><div className="a">$2,200<br /><span className="pill due" ref={pinePillRef}>Due Jun 5</span></div></div>
              </div>
            </div>

            <div className="card3d glass feed-card" data-depth="30">
              <div className="fh"><span className="live"></span> Happening now</div>
              <div>
                {feed.map((idx, pos) => {
                  const ev = FEED_EVENTS[idx];
                  return (
                    <div className={`fitem${pos === 0 ? ' hot' : ''}`} key={`${idx}-${pos}`}>
                      <span className={`ic ${ev.cls}`}>{ev.ic}</span>
                      <div>{ev.html}<small>{ev.sub}</small></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card3d glass maint-card" data-depth="22">
              <div className="mh">🔧 Leaky faucet — kitchen</div>
              <div className="ph" style={{ padding: 0, overflow: 'hidden' }}>
                <svg viewBox="0 0 214 64" width="100%" height="64" preserveAspectRatio="xMidYMid slice" aria-label="Photo of the leaky faucet">
                  <defs>
                    <linearGradient id="phbg" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#3A2B58" /><stop offset="1" stopColor="#241A3D" />
                    </linearGradient>
                  </defs>
                  <rect width="214" height="64" fill="url(#phbg)" />
                  <rect x="14" y="40" width="80" height="6" rx="3" fill="#564579" />
                  <path d="M44 40 v-14 q0 -7 7 -7 h16" stroke="#A78BFF" strokeWidth="5" fill="none" strokeLinecap="round" />
                  <rect x="64" y="15" width="10" height="8" rx="2" fill="#A78BFF" />
                  <circle cx="44" cy="14" r="4.5" fill="#7C5CFF" />
                  <path d="M69 28 q3 5 0 8 q-3 -3 0 -8z" fill="#7FB2FF" />
                  <path d="M69 44 q4 6 0 10 q-4 -4 0 -10z" fill="#7FB2FF" opacity=".75" />
                  <ellipse cx="69" cy="58" rx="9" ry="2.5" fill="#7FB2FF" opacity=".35" />
                  <rect x="150" y="44" width="52" height="13" rx="6.5" fill="rgba(255,255,255,.10)" />
                  <text x="176" y="53.5" textAnchor="middle" fontSize="8.5" fill="#CFC4E8" fontFamily="Instrument Sans, sans-serif">2 photos</text>
                </svg>
              </div>
              <div className="st"><span>Plumber scheduled</span><span className="mono" style={{ fontSize: 11 }}>THU 9AM</span></div>
              <div className="prog"><i></i></div>
            </div>
          </div>
        </div>

        <div className="marquee">
          <div className="mq-track">
            <span>Rent collected <i>✦</i> Receipts sent <i>✦</i> Repairs tracked <i>✦</i> Late fees automated <i>✦</i> Taxes export-ready <i>✦</i> Tenants pay ${TENANT_FEE} <i>✦</i></span>
            <span>Rent collected <i>✦</i> Receipts sent <i>✦</i> Repairs tracked <i>✦</i> Late fees automated <i>✦</i> Taxes export-ready <i>✦</i> Tenants pay ${TENANT_FEE} <i>✦</i></span>
          </div>
        </div>
      </header>

      {/* BENTO */}
      <div className="stat-strip">
        <div className="wrap stat-row">
          <span><b>24</b> tools in the kit</span>
          <span><b>6</b> property types</span>
          <span><b>4</b> dedicated portals</span>
          <span><b>$0</b> tenant fees, forever</span>
        </div>
      </div>

      <section className="light pad" id="features">
        <div className="wrap">
          <p className="kicker rv">What it handles</p>
          <h2 className="rv">Run the whole portfolio<br />from one screen</h2>
          <p className="lede rv">Everything below is live in the product today — residential, commercial, and housing-program portfolios, anywhere you operate. Pick a plan sized to your doors; the tenant portal is free on all of them.</p>

          <div className="bento">
            <div className="cell c-rent rv">
              <span className="dep mono">auto-deposit ✓</span>
              <h3>Rent collection</h3>
              <p>Tenants pay from their portal. Funds land in your bank, receipts send themselves.</p>
              <div className="bars"><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i></div>
            </div>
            <div className="cell c-maint rv">
              <h3>Maintenance, with photos</h3>
              <p>Requests arrive documented. Track each one to done.</p>
              <div className="mreq"><span className="th">🚿</span><div><b style={{ fontSize: 13 }}>Leaky faucet</b><br /><span style={{ fontSize: 11.5, color: 'var(--inkfade)' }}>789 Pine Rd · 2 photos</span></div><span className="flip"></span></div>
            </div>
            <div className="cell c-tax rv">
              <h3>Tax season, solved</h3>
              <p>Every expense logged all year. One click in April.</p>
              <span className="stamp">SCHEDULE E READY</span>
            </div>
            <div className="cell c-msg rv">
              <h3>Messages on record</h3>
              <div className="bubbles">
                <div className="bub them">Hi! When is the rent due?</div>
                <div className="bub me">The 1st — you can pay right in the portal 👍</div>
                <div className="dots"><i></i><i></i><i></i></div>
              </div>
            </div>
            <div className="cell c-pros rv">
              <h3>Vetted pros</h3>
              <div className="stars">★★★★★<b></b></div>
              <p className="pros-sub">Plumbers &amp; electricians reviewed by other landlords. Pay their invoices in PropMN.</p>
            </div>
            <div className="cell c-late rv">
              <h3>Late fees on autopilot</h3>
              <p>Set the grace period once. PropMN applies the fee and notifies the tenant — you never send the awkward text.</p>
              <div className="tl"><div className="go"></div><span className="node" style={{ left: '0%' }}></span><span className="node" style={{ left: '39%' }}></span><span className="node" style={{ left: '78%' }}></span></div>
              <div className="tl-l"><span>Due Jun 1</span><b>Grace ends Jun 5</b><span>Fee applied</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* FULL TOOLKIT — every item here is live in the product */}
      <section className="light pad" id="toolkit" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker rv">And the rest of the toolkit</p>
          <h2 className="rv">Yes, it does that too</h2>
          <p className="lede rv">The big six above get the spotlight, but the ledger runs deeper — homes, salons, plazas, office suites, housing programs, even in-house maintenance teams with tiered access. All of it included, none of it "coming soon."</p>
          <div className="toolkit rv">
            <div className="tk"><b><i></i>Move-in / move-out inspections</b><p>Template-driven walkthroughs, saved per property, signed off in the app.</p></div>
            <div className="tk"><b><i></i>Insurance tracking</b><p>Tenant policies on file, with automatic alerts 30 days before expiry.</p></div>
            <div className="tk"><b><i></i>Document &amp; lease vault</b><p>Leases, addenda, and shared files — stored, versioned, one tap for tenants.</p></div>
            <div className="tk"><b><i></i>Expense tracking</b><p>Log repairs, fees, and costs per property all year — tax-ready by April.</p></div>
            <div className="tk"><b><i></i>CSV &amp; Schedule E export</b><p>Your whole ledger, exportable for your accountant in one click.</p></div>
            <div className="tk"><b><i></i>Portfolio analytics &amp; rent roll</b><p>Occupancy, collections rate, delinquency aging, and a 12-month trend — with a one-click rent-roll export.</p></div>
            <div className="tk"><b><i></i>Extended-stay listings</b><p>Publish 30-day+ furnished rentals and take booking requests.</p></div>
            <div className="tk"><b><i></i>Pro invoicing, paid in-app</b><p>Service pros invoice you inside PropMN — pay by card, receipts filed.</p></div>
            <div className="tk"><b><i></i>Reviews &amp; ratings</b><p>Rate every pro you hire; see ratings from other landlords before you do.</p></div>
            <div className="tk"><b><i></i>Scheduled jobs &amp; appointments</b><p>Repairs and visits on a shared timeline — everyone knows when.</p></div>
            <div className="tk"><b><i></i>Smart notifications</b><p>Rent in, requests opened, invoices paid — pinged in-app and by email.</p></div>
            <div className="tk"><b><i></i>Receipts, forever</b><p>Every payment archived with a downloadable receipt — for both sides.</p></div>
            <div className="tk"><b><i></i>Two-factor security</b><p>2FA on your account, encrypted everything, audit-logged actions.</p></div>
            <div className="tk"><b><i></i>Vouchers &amp; housing assistance</b><p>Split rent between agency and tenant, track HAP checks, recertification &amp; inspection reminders.</p></div>
            <div className="tk"><b><i></i>Commercial units &amp; chair rental</b><p>Salons, suites, storefronts — name every space, each renter pays their own rent.</p></div>
            <div className="tk"><b><i></i>Per-property due days</b><p>Rent due the 1st or the 15th — reminders and late fees follow each property's schedule.</p></div>
            <div className="tk"><b><i></i>One-click lease PDFs</b><p>Generate a signature-ready lease or chair-rental agreement from any tenancy — subsidy clauses included.</p></div>
            <div className="tk"><b><i></i>Lease renewals, e-signed</b><p>Offer new terms; tenants sign in their portal and the new rent takes effect on the start date — never mid-month.</p></div>
            <div className="tk"><b><i></i>Offline &amp; HAP payment recording</b><p>Housing-authority checks, cash, bank transfers — recorded to the ledger with zero platform fee.</p></div>
            <div className="tk"><b><i></i>Audit-ready program reports</b><p>HAP vs tenant portions, case numbers, recert &amp; inspection countdowns — exported for the agency in one click.</p></div>
            <div className="tk"><b><i></i>Online rental applications</b><p>Share one link per property; prospects apply from their phone — income, move-in date, and voucher details land in your review queue.</p></div>
            <div className="tk"><b><i></i>Tenant screening &amp; background checks</b><p>Criminal, eviction &amp; identity screening through the applicant-consent flow — flat $10 platform fee per check.</p></div>
            <div className="tk"><b><i></i>Verifiable rent history</b><p>Tenants generate official payment-record letters anyone can verify online — fraud-proof references.</p></div>
            <div className="tk"><b><i></i>Agency portal</b><p>Housing authorities get a read-only, real-time window into the tenancies they fund.</p></div>
          </div>
        </div>
      </section>

      {/* PORTAL */}
      <section className="light pad" id="every-door" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker rv">Every kind of property</p>
          <h2 className="rv">If it has a door,<br />PropMN can run it</h2>
          <p className="lede rv">Most software stops at houses and apartments. PropMN was built for everything landlords actually own — each property type gets its own units, lease terms, due dates, and per-tenant ledger.</p>
          <div className="toolkit rv">
            <div className="tk"><b><i></i>Houses &amp; apartments</b><p>Single-family, duplexes, multi-unit buildings — the classics, with rent, repairs, and receipts on rails.</p></div>
            <div className="tk"><b><i></i>Salons, spas &amp; barbershops</b><p>Rent by the chair, booth, or suite. Each stylist gets their own ledger, lease, and due date — no more envelope of cash on Fridays.</p></div>
            <div className="tk"><b><i></i>Retail &amp; office space</b><p>Commercial leases with custom terms, units like "Suite 204," and rent due on the 1st, 5th, or whenever the lease says.</p></div>
            <div className="tk"><b><i></i>Shopping plazas &amp; mixed-use</b><p>A nail salon, a tax office, and two apartments in one building? One property, every tenant tracked separately.</p></div>
            <div className="tk"><b><i></i>Extended stay &amp; mid-term</b><p>Furnished 30+ day rentals with public listings, booking requests, and the same automated rent collection.</p></div>
            <div className="tk"><b><i></i>Voucher &amp; assisted housing</b><p>HAP and tenant portions split automatically, recertification reminders, and audit-ready agency reports built in.</p></div>
          </div>
        </div>
      </section>

      <section className="light pad" id="who-its-for" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker rv">Who it&apos;s for</p>
          <h2 className="rv">From one door<br />to a whole housing authority</h2>
          <div className="toolkit rv">
            <div className="tk"><b><i></i>Individual landlords</b><p>One duplex or twelve doors — rent, repairs, screening, and taxes handled, starting at {dollars(starter.price)}/mo with a {TRIAL_DAYS}-day free trial.</p><button className="tk-cta" onClick={() => onAuth('landlord', 'register')}>Start free →</button></div>
            <div className="tk"><b><i></i>Property management companies</b><p>Organization accounts with staff seats and tiered access — your technicians, leads, and managers each see exactly what they should.</p><button className="tk-cta" onClick={() => onAuth('landlord', 'register')}>Start free →</button></div>
            <div className="tk"><b><i></i>Enterprise &amp; white-label</b><p>Custom limits, onboarding, and dedicated support for large portfolios. Pricing built around your book, not our tiers.</p><a className="tk-cta" href="/enterprise">Request setup →</a></div>
            <div className="tk"><b><i></i>Government &amp; housing authorities</b><p>Read-only agency terminals, audit-proof ledgers, and Housing Choice Voucher workflows built in — not bolted on. Free for agencies.</p><a className="tk-cta" href="/for-agencies">For agencies →</a></div>
          </div>
        </div>
      </section>

      <section className="pad" id="workforce" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker rv">Workforce housing</p>
          <h2 className="rv">Placing crews, nurses<br />&amp; contractors — handled</h2>
          <p className="lede rv">Companies placing traveling teams need 30+ day furnished stays without hotel pricing or lease paperwork. PropMN&apos;s extended-stay marketplace lists furnished mid-term rentals with booking requests and the same automated rent collection landlords already use — one system from listing to checkout.</p>
          <div className="toolkit rv">
            <div className="tk"><b><i></i>30+ day furnished stays</b><p>Public listings with monthly pricing, photos, and availability — built for crews, travel nurses, and relocations.</p></div>
            <div className="tk"><b><i></i>Booking requests, not leases</b><p>Companies request dates, landlords approve, and rent runs on the same autopilot as everything else.</p></div>
            <div className="tk"><b><i></i>One ledger for the whole stay</b><p>Every payment recorded and receipted — clean books for the landlord, clean expense records for the employer.</p></div>
          </div>
          <div className="rv" style={{ marginTop: 26 }}><a className="tk-cta big" href="/extended-stay">Browse workforce-ready stays →</a></div>
        </div>
      </section>

      <section className="light pad" id="agencies" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker rv">For housing agencies &amp; local government</p>
          <h2 className="rv">Built for the people<br />who run the programs</h2>
          <p className="lede rv">Every payment record is generated from processed transactions — neither landlords nor tenants can edit history. That's what makes assisted housing transparent for everyone.</p>
          <div className="toolkit rv">
            <div className="tk"><b><i></i>Verifiable rent history</b><p>Tenants generate an official payment-record letter; any agency or landlord verifies it online in seconds — no account needed.</p></div>
            <div className="tk"><b><i></i>Split-payment HAP ledger</b><p>Agency portion and tenant portion tracked separately, reconciled monthly, exported audit-ready with case numbers.</p></div>
            <div className="tk"><b><i></i>Compliance that runs itself</b><p>Recertification and inspection countdowns, 30- and 7-day reminders, late fees that follow each property's own rules.</p></div>
            <div className="tk"><b><i></i>Audit-logged everything</b><p>Every sensitive action — payments recorded, access changed, tenants added — written to an immutable audit trail.</p></div>
            <div className="tk"><b><i></i>Tenant protections by default</b><p>Free portal, permanent receipts, data deletion rights, 2FA — tenants keep their records even between homes.</p></div>
            <div className="tk"><b><i></i>Program-agnostic</b><p>Housing Choice Vouchers, HUD-VASH, LIHTC, Rapid Re-Housing, EHV — or any local program, on any property type.</p><a className="tk-cta" href="/for-agencies">Why agencies choose PropMN →</a></div>
          </div>
        </div>
      </section>

      <section className="pad portal-sec" id="portal">
        <div className="aurora2"></div>
        <div className="wrap p-grid">
          <div style={{ position: 'relative' }}>
            <div className="phone">
              <div className="notch"></div>
              <div className="ph-body">
                <div className="hi">Hi Sarah 👋 · 456 Oak Ave</div>
                <div className="duelabel">Rent due</div>
                <div className="due">$1,800</div>
                <a className="ph-pay" href="#portal" onClick={(e) => e.preventDefault()}>Pay now</a>
                <div className="ph-row"><a href="#portal" onClick={(e) => e.preventDefault()}>🔧 Repair</a><a href="#portal" onClick={(e) => e.preventDefault()}>💬 Message</a></div>
                <div className="ph-hist">
                  <div className="r"><span>May rent</span><span className="mono">Paid ✓</span></div>
                  <div className="r"><span>April rent</span><span className="mono">Paid ✓</span></div>
                </div>
              </div>
            </div>
            <span className="chip ch1">Tenants pay <span className="mono">${TENANT_FEE}</span> in fees</span>
            <span className="chip ch2">⚡ Pay in two taps</span>
            <span className="chip ch3">📄 Every receipt, forever</span>
          </div>
          <div>
            <p className="kicker rv" style={{ color: 'var(--brand-hi)' }}>The other half of the ledger</p>
            <h2 className="rv">A portal your tenants<br /><span className="serif" style={{ color: 'var(--brand-hi)' }}>actually</span> use</h2>
            <ul className="portal-list rv">
              <li><b>Pay rent online</b> — no fees, no app to install</li>
              <li><b>Photo repair requests</b> — documented in under a minute</li>
              <li><b>Receipts &amp; lease</b> — always one tap away</li>
            </ul>
            <p className="zero rv">Free for every tenant, on every plan — <span className="mono">${TENANT_FEE} forever.</span> Tenants who like the system pay on the 1st.</p>
          </div>
        </div>
      </section>

      {/* STATS / QUOTE */}
      <section className="light pad">
        <div className="wrap stats-grid">
          <div className="rv">
            <p className="bigquote">Built for the landlord with a <span className="serif">day job</span> — not the REIT with a back office.</p>
            <p className="q-by">Rent, repairs, and records for portfolios of 1 to {(livePlans.landlord_enterprise?.maxProperties ?? FALLBACK_PLANS.landlord_enterprise.maxProperties)}+ doors.</p>
          </div>
          <div className="statrow rv">
            <div className="statbox"><div className="n g" data-count={TENANT_FEE} data-prefix="$">${TENANT_FEE}</div><div className="d">in fees, ever, for your tenants</div></div>
            <div className="statbox"><div className="n" data-count={TRIAL_DAYS} data-suffix=" days">0 days</div><div className="d">free trial — cancel anytime</div></div>
            <div className="statbox"><div className="n p" data-count={(livePlans.landlord_enterprise?.maxProperties ?? FALLBACK_PLANS.landlord_enterprise.maxProperties)} data-suffix="+">0+</div><div className="d">doors supported — custom plans go unlimited</div></div>
            <div className="statbox"><div className="n g" data-count="0" data-prefix="$">$0</div><div className="d">setup fees — just the monthly plan</div></div>
          </div>
        </div>
      </section>

      {/* PRICING — top 3 tiers; full grid lives on the pricing page */}
      <section className="light pad" id="pricing-teaser" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker rv">Pricing</p>
          <h2 className="rv">Clear pricing, sized<br />to your portfolio</h2>
          <div className="plans rv">
            {[starter, pro, biz].map((tier) => (
              <div className={`plan${tier.popular ? ' plan-hot' : ''}`} key={tier.id}>
                {tier.popular && <span className="hot-tag">MOST POPULAR</span>}
                <div className="tier">{tier.name}</div>
                <div className="for">{tier.description}</div>
                <div className="pr">{dollars(tier.price)}<span>/mo</span></div>
                <ul>{tier.features.slice(0, 5).map((f) => <li key={f}>{f}</li>)}</ul>
                <button className="go" onClick={() => onAuth('landlord', 'register')}>Start free</button>
              </div>
            ))}
          </div>
          <div className="all-plans rv">
            <button onClick={() => navigateTo('pricing')}>See all plans, Enterprise &amp; service-provider pricing →</button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="blob cb1"></div>
        <div className="wrap">
          <h2 className="rv">Your next rent day,<br /><span className="serif" style={{ color: 'var(--brand-hi)' }}>on autopilot.</span></h2>
          <p className="lede rv" style={{ maxWidth: '44ch' }}>Set up your first property tonight. Your tenant gets an invite — you get your evenings back.</p>
          <div className="hero-ctas rv"><button className="btn-hero" onClick={() => onAuth('landlord', 'register')}>Start your {TRIAL_DAYS}-day free trial</button></div>
          <small className="rv">{TRIAL_DAYS}-day free trial · cancel anytime · tenants always free</small>
        </div>
      </section>

      <footer>
        <div className="wrap foot">
          <span className="logo"><img src="/propmn-logo.png" alt="PropMN" style={{ height: 24 }} /></span>
          <div className="links">
            <button onClick={() => scrollTo('features')}>How it works</button>
            <button onClick={() => navigateTo('pricing')}>Pricing</button>
            <button onClick={() => navigateTo('service-providers')}>Service pros</button>
            <a href="/extended-stay">Extended stay</a>
            <a href="/housing-vouchers">Housing vouchers</a>
            <a href="/how-it-works">How it works</a> · <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
          </div>
          <div>support@propmn.com · © 2026</div>
        </div>
      </footer>
    </div>
  );
}
